import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';

import {
  type FilterChipDateRangeDef,
  type FilterChipDef,
  type FilterToolbarSchema,
  type FilterToolbarValue,
  UNSET,
} from './types';

type ChipParser = typeof parseAsString | ReturnType<typeof parseAsArrayOf<string>>;

/**
 * Resolves the in-memory "default" value for a chip. Used to:
 *   - hydrate the toolbar value when a chip's URL key is absent
 *   - decide whether a chip is "active" (== contributes to activeCount)
 *   - implement clear-all by writing each chip back to this value
 */
const getChipDefault = (def: FilterChipDef): string | string[] => {
  if (def.type === 'combobox' && def.multi) return [];
  return def.defaultValue ?? UNSET;
};

const isMultiCombobox = (def: FilterChipDef): boolean => def.type === 'combobox' && def.multi === true;

/**
 * Resolves a write to the value that will actually land in the URL, where `null` means "key
 * removed". Default and empty values are stripped so unset filters never appear in the query
 * string.
 */
const normalizeWrite = (chip: FilterChipDef | undefined, next: string | string[] | null): string | string[] | null => {
  if (next === null) return null;
  if (Array.isArray(next)) return next.length === 0 ? null : next;
  if (next === '' || next === UNSET) return null;
  if (chip) {
    const fallback = getChipDefault(chip);
    if (!Array.isArray(fallback) && next === fallback) return null;
  }
  return next;
};

/** Value-equality across the shapes a single URL key can hold. */
const isSameWrite = (a: string | string[] | null, b: string | string[] | null): boolean => {
  if (a === null || b === null) return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => v === b[i]);
  return a === b;
};

const isChipActive = (def: FilterChipDef, value: string | string[]): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  const fallback = getChipDefault(def);
  if (Array.isArray(fallback)) return value.length > 0;
  return value !== fallback && value !== UNSET && value !== '';
};

/** Resolve the URL keys for a date-range chip. Defaults to `${name}From` / `${name}To`. */
export const resolveDateKeys = (def: FilterChipDateRangeDef): { fromKey: string; toKey: string } => ({
  fromKey: def.fromKey ?? `${def.name}From`,
  toKey: def.toKey ?? `${def.name}To`,
});

interface UseFilterToolbarStateResult {
  value: FilterToolbarValue;
  set: (name: string, next: string | string[] | null) => void;
  setMany: (patch: Record<string, string | string[] | null>) => void;
  clearAll: () => void;
  activeCount: number;
  isActive: (name: string) => boolean;
}

/**
 * Schema-driven URL state for `FilterToolbar`.
 *
 * Builds a `nuqs` `useQueryStates` parser map from the schema, then exposes a
 * normalized `value` object plus mutators. The hook is opinionated about a
 * few things in service of keeping URLs clean:
 *
 *   - Default/empty chip values are stripped from the URL on write.
 *   - Multi-combobox chips read/write as `?key=a&key=b`.
 *   - Date-range chips manage two URL keys (resolved via `resolveDateKeys`)
 *     holding ISO strings; both absent ↔ no date filter active.
 *   - When `schema.pageKey` is set, a filter change that alters a value also
 *     clears that key, so results restart at the first page.
 *
 * Search debouncing is not handled here — `FilterToolbar.Search` owns its
 * local input state and calls `set(searchName, debouncedValue)` itself.
 */
export const useFilterToolbarState = (schema: FilterToolbarSchema): UseFilterToolbarStateResult => {
  // ── Build the nuqs parser map from the schema ───────────────────────────
  const parsers = useMemo(() => {
    // Mix of string and array-of-string nuqs parsers per chip.
    const map: Record<string, ChipParser> = {};

    if (schema.search) {
      map[schema.search.name] = parseAsString;
    }

    schema.chips.forEach((chip) => {
      if (isMultiCombobox(chip)) {
        map[chip.name] = parseAsArrayOf(parseAsString);
      } else {
        map[chip.name] = parseAsString;
      }
    });

    if (schema.date) {
      const { fromKey, toKey } = resolveDateKeys(schema.date);
      map[fromKey] = parseAsString;
      map[toKey] = parseAsString;
    }

    // Registered only so it can be cleared alongside a filter write. We never write a value to it,
    // just `null`, so the string parser is enough — the owning page parses the page number itself.
    if (schema.pageKey) {
      map[schema.pageKey] = parseAsString;
    }

    return map;
  }, [schema]);

  const [urlState, setUrlState] = useQueryStates(parsers);

  // ── Compose the in-memory value object (urlState + chip defaults) ───────
  const value: FilterToolbarValue = useMemo(() => {
    const out: FilterToolbarValue = {};

    if (schema.search) {
      out[schema.search.name] = (urlState[schema.search.name] as string | null) ?? '';
    }

    schema.chips.forEach((chip) => {
      const fallback = getChipDefault(chip);
      const raw = urlState[chip.name];

      if (isMultiCombobox(chip)) {
        out[chip.name] = (raw as string[] | null) ?? (fallback as string[]);
      } else {
        const single = (raw as string | null) ?? null;
        out[chip.name] = single ?? (fallback as string);
      }
    });

    if (schema.date) {
      const { fromKey, toKey } = resolveDateKeys(schema.date);
      out[fromKey] = (urlState[fromKey] as string | null) ?? '';
      out[toKey] = (urlState[toKey] as string | null) ?? '';
    }

    return out;
  }, [urlState, schema]);

  // ── Mutators ────────────────────────────────────────────────────────────
  const current = useCallback(
    (name: string): string | string[] | null => (urlState[name] as string | string[] | null) ?? null,
    [urlState],
  );

  const set = useCallback(
    (name: string, next: string | string[] | null) => {
      const chip = schema.chips.find((c) => c.name === name);
      const resolved = normalizeWrite(chip, next);
      const patch: Record<string, string | string[] | null> = { [name]: resolved };

      // Reset pagination only when the value genuinely changed. `ToolbarSearch` re-commits its
      // current value on mount, so an unconditional reset would drop `?page=` on every page load
      // and break deep links and back-navigation.
      if (schema.pageKey && !isSameWrite(resolved, current(name))) {
        patch[schema.pageKey] = null;
      }

      void setUrlState(patch);
    },
    [schema, setUrlState, current],
  );

  const setMany = useCallback(
    (patch: Record<string, string | string[] | null>) => {
      const next: Record<string, string | string[] | null> = {};
      let changed = false;

      Object.entries(patch).forEach(([key, val]) => {
        const chip = schema.chips.find((c) => c.name === key);
        const resolved = normalizeWrite(chip, val);
        next[key] = resolved;
        if (!isSameWrite(resolved, current(key))) changed = true;
      });

      if (schema.pageKey && changed) next[schema.pageKey] = null;

      void setUrlState(next);
    },
    [schema, setUrlState, current],
  );

  const clearAll = useCallback(() => {
    const patch: Record<string, null> = {};
    if (schema.search) patch[schema.search.name] = null;
    schema.chips.forEach((chip) => {
      patch[chip.name] = null;
    });
    if (schema.date) {
      const { fromKey, toKey } = resolveDateKeys(schema.date);
      patch[fromKey] = null;
      patch[toKey] = null;
    }
    if (schema.pageKey) patch[schema.pageKey] = null;
    void setUrlState(patch);
  }, [schema, setUrlState]);

  // ── Activeness ──────────────────────────────────────────────────────────
  const activeCount = useMemo(() => {
    let count = 0;
    if (schema.search && (value[schema.search.name] as string)?.length > 0) count++;
    schema.chips.forEach((chip) => {
      if (isChipActive(chip, value[chip.name] as string | string[])) count++;
    });
    if (schema.date) {
      const { fromKey, toKey } = resolveDateKeys(schema.date);
      if (((value[fromKey] as string) ?? '') !== '' || ((value[toKey] as string) ?? '') !== '') count++;
    }
    return count;
  }, [schema, value]);

  const isActive = useCallback(
    (name: string): boolean => {
      const chip = schema.chips.find((c) => c.name === name);
      if (chip) return isChipActive(chip, value[name] as string | string[]);
      if (schema.date && schema.date.name === name) {
        const { fromKey, toKey } = resolveDateKeys(schema.date);
        return ((value[fromKey] as string) ?? '') !== '' || ((value[toKey] as string) ?? '') !== '';
      }
      if (schema.search?.name === name) return ((value[name] as string) ?? '').length > 0;
      return false;
    },
    [schema, value],
  );

  return { value, set, setMany, clearAll, activeCount, isActive };
};

export { getChipDefault, isChipActive };
